<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { PACKAGE, HOTEL, AIR, MDT } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ExclusionEmailModal from '@/components/modal/ExclusionEmailModal.vue';
import EmailConfigNumber from '@/components/configuration/EmailConfigNumber.vue';
import EmailConfigExclusions from '@/components/configuration/EmailConfigExclusions.vue';
import PromotionExpiry from '@/components/admin/ExpiryDateConfig/PromotionExpiry.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const adminSetupStore = useAdminSetupStore();
const packageManagementStore = usePackageManagementStore();

const isLoading = ref(false);
const listConfigDate = ref([]);
const specialPromotionConfig = ref<any>([]);
const destinations = ref<any>([]);
const expiredConfig = computed(() => adminSetupStore.expiredConfig);

const moveToEnd = (array: any, index: number) => {
  if (index < 0 || index >= array.length) {
    return;
  }
  const [item] = array.splice(index, 1);
  array.push(item);
};

const headers = ref(['', t('quote_config.days_sixty_plus'), t('quote_config.days_range_sixty')]);
const renderHeader = (type: string) => {
  switch (type) {
    case PACKAGE:
      return t('management.email.add_package_exception');
    case HOTEL:
      return t('management.email.add_hotel_exception');
    case AIR:
      return t('management.email.add_air_exception');
    case MDT:
      return t('management.email.add_mdt_exception');
    default:
      return t('management.email.add_cruise_exception');
  }
};

const splitArray = (listData: any) => {
  const currentData = listData[0] ? listData[0] : listData;
  listConfigDate.value = [];
  for (const key in currentData) {
    let dataObject = {};
    if (!key.startsWith('email')) {
      dataObject = { ...currentData[key], keyName: key };
      listConfigDate.value.push(dataObject);
    }
  }
  moveToEnd(listConfigDate.value, 1);
};

const updateDataSubmit = (dataSubmit: any) => {
  return dataSubmit?.reduce((acc: any, item: any) => {
    if (item.code.trim()) {
      acc[item.code] = item.expiryDay || 0;
    }
    return acc;
  }, {});
};

const updateDataException = (expiryDateList: any, type: string) => {
  switch (type) {
    case PACKAGE:
      expiredConfig.value.packageExp.exclusions = updateDataSubmit(expiryDateList);
      break;
    case HOTEL:
      expiredConfig.value.hotelExp.exclusions = updateDataSubmit(expiryDateList);
      break;
    case AIR:
      expiredConfig.value.airExp.exclusions = updateDataSubmit(expiryDateList);
      break;
    case MDT:
      expiredConfig.value.mdtExp.exclusions = updateDataSubmit(expiryDateList);
      break;
    default:
      expiredConfig.value.cruiseExp.exclusions = updateDataSubmit(expiryDateList);
      break;
  }
  splitArray(expiredConfig.value);
};

const handleUpdateExpired = (emailDataExclusion: any, expiryDateList?: any, isExcluse?: boolean) => {
  const getPayload = {
    type: emailDataExclusion.type,
    day: emailDataExclusion.emailType?.dayDefault,
    dayFromDeparture: emailDataExclusion.emailType?.dayFromDeparture ?? 0,
    exclusions: updateDataSubmit(expiryDateList)
  };
  adminSetupStore
    .updateExpiredConfig(isExcluse ? getPayload : emailDataExclusion)
    .then(() => {
      adminSetupStore.getExpiredConfig().then((result) => {
        splitArray(result);
      });
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const onOpenModal = (emailDataExclusion: any, isExcluse: boolean = true) => {
  const dialogRef = dialog.open(ExclusionEmailModal, {
    data: {
      expiryDate: emailDataExclusion.emailType.exclusions,
      type: emailDataExclusion.type
    },
    props: {
      header: renderHeader(emailDataExclusion.type),
      modal: true,
      style: {
        width: '45vw'
      },
      breakpoints: {
        '960px': '60vw',
        '640px': '90vw'
      }
    },
    emits: {
      isSave: async (expiryDateList: any) => {
        await handleUpdateExpired(emailDataExclusion, expiryDateList, isExcluse);
        updateDataException(expiryDateList, emailDataExclusion.type);
        dialogRef.close(true);
      }
    }
  });
};

const formatData = (config: any) => {
  return config.map((item: any) => {
    return {
      ...item,
      brochures: item?.brochures?.join(','),
      destinations: item?.destinations?.join(',')
    };
  });
};

const onSaveConfig = (data: any) => {
  isLoading.value = true;
  const payload = {
    specialPromos: formatData(data) || []
  };

  adminSetupStore
    .updateSpecialPromotionConfiguration(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      getSpecialConfiguration();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getSpecialConfiguration = () => {
  isLoading.value = true;
  adminSetupStore
    .getSpecialPromotionConfiguration()
    .then((res: any) => {
      specialPromotionConfig.value = res.data?.specialPromos;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getExpiredConfig = () => {
  isLoading.value = true;
  adminSetupStore
    .getExpiredConfig()
    .then((result) => {
      splitArray(result);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getListDestination = () => {
  isLoading.value = true;
  packageManagementStore
    .getListDestination()
    .then((res: any) => {
      destinations.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getExpiredConfig();
getSpecialConfiguration();
getListDestination();
</script>
<template>
  <div class="admin-setup expiry-date-config-management">
    <LoadingSpinner v-if="isLoading" />
    <div class="card">
      <h4>{{ $t('management.quote_config.expiry_date_configuration') }}</h4>
      <div class="flex flex-wrap justify-content-between mt-5">
        <div>
          <h5>{{ $t('management.quote_config.default_expiry_by_quote_type') }}</h5>
          <div class="flex mb-2 mt-4">
            <div :class="{ 'w-20rem': !header, 'cell-width': header }" class="ml-1" v-for="header in headers" :key="header">
              <b>{{ header }}</b>
            </div>
          </div>
          <div class="config-number" v-for="(item, emailIndex) in listConfigDate" :key="emailIndex">
            <EmailConfigNumber :isExpiryDateDeparture="true" :emailData="item" @handleSubmit="(payload) => handleUpdateExpired(payload)"></EmailConfigNumber>
          </div>
        </div>
        <div class="seperator mb-3 mt-5"></div>
        <div>
          <h5>{{ $t('management.quote_config.package_promotion_expiry') }}</h5>
          <div>
            <PromotionExpiry @onSaveConfig="(data) => onSaveConfig(data)" :destinations="destinations" :specialPromotionConfig="specialPromotionConfig" />
          </div>
        </div>
        <div class="seperator mb-3 mt-5"></div>
        <div>
          <h5>{{ $t('management.quote_config.expiry_exclusions_by_destination') }}</h5>
          <div class="config-exclusion" v-for="(item, emailIndex) in listConfigDate" :key="emailIndex">
            <EmailConfigExclusions :emailData="item" @handleOpenModal="(payload) => onOpenModal(payload)"></EmailConfigExclusions>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
